import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getSupplierBids } from '../../../../services/procurementService';
import moment from 'moment';
import 'moment/locale/pt'; // Import supported locales
import 'moment/locale/tet';
import { useTranslation } from 'react-i18next';

const BiddingComponent = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || t('allBids');

  const [searchTerm, setSearchTerm] = useState('');
  const [activeStatus, setActiveStatus] = useState(initialStatus);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const supplierId = localStorage.getItem('supplierId') || '';

  const statusMap = {
    [t('allBids')]: 0,
    [t('inProgress')]: 1,
    [t('active')]: 2,
    [t('denied')]: 3,
  };

  // Set moment.js locale whenever the language changes
  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);

  const { data: allBids = { data: [], pageNumber: 1, pageSize: 10, totalRecords: 0 } } = useQuery({
    queryKey: ['supplierBids', activeStatus],
    queryFn: async () => {
      if (!supplierId) throw new Error('Supplier ID is missing.');
      try {
        const response = await getSupplierBids(supplierId, currentPage, pageSize);
        return response;
      } catch (error) {
        toast.error(t('fetchError'));
        throw error;
      }
    },
    enabled: !!supplierId,
    refetchOnWindowFocus: false,
  });

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusChange = (status: string) => {
    setActiveStatus(status);
    setCurrentPage(1);
    setSearchParams({ tab: 'Bidding', status });
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  return (
    <div className="min-h-[85vh] rounded-2xl border border-[#E5E5E5] bg-white p-8">
      <div className="relative mb-6">
        <input
          type="text"
          placeholder={t('searchPlaceholder')}
          value={searchTerm}
          onChange={handleSearchChange}
          className="w-full rounded-lg border border-[#D0D5DD] bg-white px-3.5 py-2.5 pl-12 text-base text-gray-800"
        />
        <Search
          className="absolute left-4 top-1/2 -translate-y-1/2 transform text-gray-500"
          size={20}
        />
      </div>

      <div className="mb-6 flex space-x-10">
        {Object.keys(statusMap).map((status) => (
          <button
            key={status}
            className={`min-w-[106px] rounded-full px-3.5 py-2.5 text-base ${
              activeStatus === status
                ? 'bg-green-600 font-semibold text-white'
                : 'bg-none text-gray-700 hover:bg-gray-300'
            }`}
            onClick={() => handleStatusChange(status)}
          >
            {status}
          </button>
        ))}
      </div>

      {allBids.data.length > 0 ? (
        <div className="overflow-auto rounded-xl border border-[#E4E7EC] shadow-sm">
          <div className="grid grid-cols-[1.3fr_.5fr_.5fr_.5fr] gap-4 rounded-t-xl border-b border-[#D2D6DB] bg-[#F5F5F5] px-5 py-[14px] text-sm font-semibold text-[#40474F]">
            <span>{t('contractName')}</span>
            <span>{t('type')}</span>
            <span>{t('date')}</span>
            <span>{t('status')}</span>
          </div>

          {allBids.data.map((bidItem) => (
            <Link
              key={bidItem.tenderBidId}
              to={`/procurement/${bidItem?.serviceInformationId}/bids/${bidItem?.tenderBidId}`}
              className="grid grid-cols-[1.3fr_.5fr_.5fr_.5fr] items-center gap-4 border-b border-[#D2D6DB] px-5 py-[14px]"
            >
              <span
                className="overflow-hidden truncate whitespace-nowrap text-base font-semibold text-[#101828]"
                title={bidItem?.title}
              >
                {bidItem?.title}
              </span>
              <span className="text-sm font-medium text-[#525252]">{bidItem?.businessType}</span>
              <span className="text-sm font-medium text-[#525252]">
                {moment(bidItem?.bidEndDate).format('DD/MM/YYYY')}
              </span>
              <span
                className={`w-fit min-w-[93px] rounded-[60px] px-2.5 py-1 text-center text-sm font-medium text-white ${
                  bidItem?.status === 'New' || bidItem?.status === 'Approved'
                    ? 'bg-[#24A148]'
                    : bidItem?.status === 'Processing'
                      ? 'bg-[#134C9D]'
                      : 'bg-[#FA4D56]'
                }`}
              >
                {t(bidItem?.status.toLowerCase())}
              </span>
            </Link>
          ))}

          <div className="flex items-center justify-between px-6 pb-4 pt-3">
            <span className="text-sm text-[#344054]">
              {t('pageInfo', {
                current: currentPage,
                total: Math.ceil(allBids.totalRecords / pageSize),
              })}
            </span>
            <div className="flex gap-x-3">
              <button
                className="rounded-lg border border-[#D0D5DD] px-3.5 py-2 text-sm font-semibold text-[#344054] shadow-sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                {t('previous')}
              </button>
              <button
                className="rounded-lg border border-[#D0D5DD] px-3.5 py-2 text-sm font-semibold text-[#344054] shadow-sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage * pageSize >= allBids.totalRecords}
              >
                {t('next')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex min-h-[40vh] items-center justify-center">
          <div>
            <h3 className="text-center text-2xl font-semibold text-[#161616]">
              {t('noBidsAvailable')}
            </h3>
            <p className="text-center text-sm font-medium text-[#7F7F7F]">{t('bidsUnavailable')}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BiddingComponent;
